import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import jobService from '../../services/jobService';

// Validation schema for basic info
const basicInfoSchema = z.object({
  hospitalId: z.string().min(1, 'Selecione um hospital'),
  locationId: z.string().min(1, 'Selecione um local'),
  titulo: z.string().min(3, 'Título deve ter no mínimo 3 caracteres'),
  descricao: z.string().optional(),
  dataPrevista: z.string().min(1, 'Selecione uma data'),
});

/**
 * StepBasicInfo Component
 * First step of job creation wizard - collects basic job information
 */
export function StepBasicInfo({ formData, updateFormData, onNext }) {
  const [hospitals, setHospitals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      hospitalId: formData.hospitalId?.toString() || '',
      locationId: formData.locationId?.toString() || '',
      titulo: formData.titulo || '',
      descricao: formData.descricao || '',
      dataPrevista: formData.dataPrevista || '',
    },
  });

  const selectedHospitalId = watch('hospitalId');

  // Load initial data (hospitals only)
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const hospitalsData = await jobService.getHospitals();
        setHospitals(hospitalsData);

        // If hospital was already selected, load its locations
        if (formData.hospitalId) {
          const locationsData = await jobService.getLocations(formData.hospitalId);
          setLocations(locationsData);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        setLoadError('Erro ao carregar dados. Tente novamente.');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [formData.hospitalId]);

  // Load locations when hospital changes
  useEffect(() => {
    if (selectedHospitalId && selectedHospitalId !== formData.hospitalId?.toString()) {
      const loadLocations = async () => {
        try {
          const locationsData = await jobService.getLocations(parseInt(selectedHospitalId));
          setLocations(locationsData);

          // Reset location selection when hospital changes
          setValue('locationId', '');
        } catch (error) {
          console.error('Failed to load locations:', error);
          setLocations([]);
        }
      };

      loadLocations();
    } else if (!selectedHospitalId) {
      setLocations([]);
      setValue('locationId', '');
    }
  }, [selectedHospitalId, formData.hospitalId, setValue]);

  const onSubmit = (data) => {
    // Convert string IDs to numbers
    updateFormData({
      hospitalId: parseInt(data.hospitalId),
      locationId: parseInt(data.locationId),
      titulo: data.titulo,
      descricao: data.descricao || '',
      dataPrevista: data.dataPrevista,
    });
    onNext();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dados...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationCircleIcon className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <p className="text-red-800 font-medium mb-2">{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Hospital */}
      <div>
        <label htmlFor="hospitalId" className="block text-sm font-semibold text-gray-700 mb-2">
          Hospital *
        </label>
        <select
          id="hospitalId"
          {...register('hospitalId')}
          className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all ${
            errors.hospitalId
              ? 'border-red-300 focus:border-red-500'
              : 'border-gray-200 focus:border-blue-500'
          }`}
        >
          <option value="">Selecione um hospital</option>
          {hospitals.map((hospital) => (
            <option key={hospital.id} value={hospital.id}>
              {hospital.nome_fantasia} ({hospital.codigo})
            </option>
          ))}
        </select>
        {errors.hospitalId && (
          <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
            <ExclamationCircleIcon className="w-4 h-4" />
            {errors.hospitalId.message}
          </p>
        )}
      </div>

      {/* Location */}
      <div>
        <label htmlFor="locationId" className="block text-sm font-semibold text-gray-700 mb-2">
          Local *
        </label>
        <select
          id="locationId"
          {...register('locationId')}
          disabled={!selectedHospitalId}
          className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed ${
            errors.locationId
              ? 'border-red-300 focus:border-red-500'
              : 'border-gray-200 focus:border-blue-500'
          }`}
        >
          <option value="">Selecione um local</option>
          {locations.map((location) => (
            <option key={location.id} value={location.id}>
              {location.nome}
            </option>
          ))}
        </select>
        {errors.locationId && (
          <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
            <ExclamationCircleIcon className="w-4 h-4" />
            {errors.locationId.message}
          </p>
        )}
        {!selectedHospitalId && (
          <p className="mt-1.5 text-sm text-gray-500">Selecione um hospital primeiro</p>
        )}
      </div>

      {/* Title */}
      <div>
        <label htmlFor="titulo" className="block text-sm font-semibold text-gray-700 mb-2">
          Título do Job *
        </label>
        <input
          id="titulo"
          type="text"
          {...register('titulo')}
          placeholder="Ex: Contagem Mensal - Outubro 2025"
          className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all ${
            errors.titulo
              ? 'border-red-300 focus:border-red-500'
              : 'border-gray-200 focus:border-blue-500'
          }`}
        />
        {errors.titulo && (
          <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
            <ExclamationCircleIcon className="w-4 h-4" />
            {errors.titulo.message}
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="descricao" className="block text-sm font-semibold text-gray-700 mb-2">
          Descrição (opcional)
        </label>
        <textarea
          id="descricao"
          {...register('descricao')}
          rows={3}
          placeholder="Adicione observações ou instruções para o operador"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
        />
      </div>

      {/* Expected Date */}
      <div>
        <label htmlFor="dataPrevista" className="block text-sm font-semibold text-gray-700 mb-2">
          Data Prevista *
        </label>
        <input
          id="dataPrevista"
          type="date"
          {...register('dataPrevista')}
          className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all ${
            errors.dataPrevista
              ? 'border-red-300 focus:border-red-500'
              : 'border-gray-200 focus:border-blue-500'
          }`}
        />
        {errors.dataPrevista && (
          <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
            <ExclamationCircleIcon className="w-4 h-4" />
            {errors.dataPrevista.message}
          </p>
        )}
      </div>

      {/* Next Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg shadow-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
        >
          Próximo: Selecionar Produtos →
        </button>
      </div>
    </form>
  );
}
